import { StorageFormatLegacyBuilder } from './formats/storage/legacy.js';
import serialization from './formats/serialization/index.js';
class PublicKeyStore {
    #walletAccounts;
    #hardwareWalletPublicKeysAtom;
    #softwareWalletPublicKeysAtom;
    #walletAccountsAtom;
    constructor({ walletAccounts, hardwareWalletPublicKeysAtom, softwareWalletPublicKeysAtom, walletAccountsAtom, }) {
        this.#walletAccounts = walletAccounts;
        this.#hardwareWalletPublicKeysAtom = hardwareWalletPublicKeysAtom;
        this.#softwareWalletPublicKeysAtom = softwareWalletPublicKeysAtom;
        this.#walletAccountsAtom = walletAccountsAtom;
    }
    #getWalletAccount = async (walletAccountName) => {
        const walletAccounts = await this.#walletAccountsAtom.get();
        return walletAccounts[walletAccountName];
    };
    #getAtoms = (walletAccount) => {
        if (!walletAccount) {
            return [this.#hardwareWalletPublicKeysAtom, this.#softwareWalletPublicKeysAtom];
        }
        if (walletAccount.isHardware) {
            return [this.#hardwareWalletPublicKeysAtom];
        }
        if (walletAccount.isSoftware) {
            return [this.#softwareWalletPublicKeysAtom];
        }
        throw new Error('Wallet account is unsupported');
    };
    #getData = async (walletAccount) => {
        const atoms = this.#getAtoms(walletAccount);
        const atomsData = await Promise.all(atoms.map((atom) => atom.get()));
        const data = atomsData.reduce((merged, atom) => ({ ...merged, ...atom }), {});
        return new StorageFormatLegacyBuilder({ data });
    };
    #setData = async (publicKeyStore, walletAccount) => {
        const data = publicKeyStore.serialize();
        if (walletAccount.isSoftware) {
            return this.#softwareWalletPublicKeysAtom.set(data);
        }
        if (walletAccount.isHardware) {
            return this.#walletAccounts.setAccounts(data);
        }
        throw new Error('Wallet account is unsupported');
    };
    add = async ({ walletAccount, ...key }) => {
        await this.addMany({ walletAccount, keys: [key] });
    };
    addMany = async ({ walletAccount, ...params }) => {
        const publicKeyStore = await this.#getData(walletAccount);
        const keys = params.keys.map((key) => {
            const xpub = key.xpub ? serialization.xpub.serialize(key.xpub) : undefined;
            const publicKey = key.publicKey ? serialization.publicKey.serialize(key.publicKey) : undefined;
            return { ...key, xpub, publicKey };
        });
        await publicKeyStore.addMany({ walletAccountName: walletAccount.toString(), keys });
        await this.#setData(publicKeyStore, walletAccount);
    };
    get = async (params) => {
        const walletAccount = await this.#getWalletAccount(params.walletAccountName);
        if (!walletAccount) {
            return null;
        }
        const publicKeyStore = await this.#getData(walletAccount);
        const entry = publicKeyStore.get(params);
        if (!entry) {
            return null;
        }
        const { publicKey, xpub } = entry;
        if (!publicKey && !xpub) {
            return null;
        }
        const deserialized = Object.create(null);
        if (xpub) {
            deserialized.xpub = serialization.xpub.deserialize(xpub);
        }
        if (publicKey) {
            deserialized.publicKey = serialization.publicKey.deserialize(publicKey);
        }
        return deserialized;
    };
    delete = async (params) => {
        const walletAccount = await this.#getWalletAccount(params.walletAccountName);
        if (!walletAccount) {
            throw new Error(`Wallet account not found: ${params.walletAccountName}`);
        }
        const publicKeyStore = await this.#getData(walletAccount);
        publicKeyStore.delete(params);
        await this.#setData(publicKeyStore, walletAccount);
    };
    clearSoftwareWalletAccountKeys = async () => {
        const empty = new StorageFormatLegacyBuilder().serialize();
        await this.#softwareWalletPublicKeysAtom.set(empty);
    };
}
const createPublicKeyStore = (deps) => new PublicKeyStore({ ...deps });
const publicKeyStoreDefinition = {
    id: 'publicKeyStore',
    type: 'module',
    factory: createPublicKeyStore,
    dependencies: [
        'walletAccounts',
        'hardwareWalletPublicKeysAtom',
        'softwareWalletPublicKeysAtom',
        'walletAccountsAtom',
    ],
    public: true,
};
export default publicKeyStoreDefinition;
